<template>
    <v-container>
        <v-row>
            <v-col cols="9" class="px-1 pb-0 align-content-center">
                <v-select
                    v-model="deliverableType"
                    :items="deliverable_types"
                    label="Please choose a deliverable type"
                    class="required_rule"
                    outlined
                    :hint="fileTypeHint"
                    persistent-hint
                    required
                    @change="handleDeliverableTypeChange">
                </v-select>
            </v-col>
            <v-col cols="3" class="px-1 align-content-center">
                <div class="d-flex align-center justify-end mt-n6">
                    <div class="d-inline mr-1 info--text">Generate Email:</div>
                    <v-checkbox
                        v-model="generateEmail"
                        hide-details
                        color="primary"
                        :disabled="!emailTemplateFound"
                        class="d-inline ma-0 pa-0 mr-n2">
                    </v-checkbox>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" class="px-1">
                <v-file-input
                    v-model="files"
                    :label="labelText"
                    :hint="hintText"
                    persistent-hint
                    truncate-length="300"
                    :multiple="isMultiple"
                    :accept="allowedFileTypes.join(',')"
                    @change="handleChange"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" class="px-1">
                <v-progress-linear v-if="isUploading"
                    :indeterminate="true"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" class="px-1 py-0">
                <div class="py-2 px-4" style="border: 1px solid var(--v-accent-darken1); border-radius: 8px; background-color: #FFF9EE;">
                    <div class="d-flex d-block align-baseline pb-1">
                        <v-icon 
                            class="error--text text--darken-1 d-inline mr-3">
                            mdi-alert
                        </v-icon>
                        <div class="d-inline font-weight-bold">STOP — Review Before You Upload!</div>
                        <v-icon 
                            class="error--text text--darken-1 d-inline ml-3">
                            mdi-alert
                        </v-icon>
                    </div>
                    
                    <div class="d-flex d-block align-center pt-1 mb-3 caption">Before submitting any documents for client access, please confirm the following to avoid errors or client confusion:</div>

                    <div class="d-flex d-block align-center ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-checkbox-marked
                        </v-icon>
                        <div class="d-inline caption">You're uploading the correct document — verify the file name and contents.</div>
                    </div>
                    
                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-checkbox-marked
                        </v-icon>
                        <div class="d-inline caption">You're uploading the most recent version — double-check for outdated drafts.</div>
                    </div>

                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-checkbox-marked
                        </v-icon>
                        <div class="d-inline caption">You're uploading to the right client and folder — confirm the correct recipient and destination.</div>
                    </div>

                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-close-octagon
                        </v-icon>
                        <div class="d-inline caption">Do NOT upload “Internal Only” documents — these should never be shared with clients.</div>
                    </div>

                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-close-octagon
                        </v-icon>
                        <div class="d-inline caption">Do NOT upload resources, templates, or marketing materials here — those belong in the “Resources” section.</div>
                    </div>

                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-eye-check
                        </v-icon>
                        <div class="d-inline caption">Review formatting and professionalism — the document should reflect SRG's quality standards.</div>
                    </div>

                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-lock
                        </v-icon>
                        <div class="d-inline caption">Ensure no confidential or unrelated information is included by mistake.</div>
                    </div>

                    <div class="d-flex d-block align-center pt-1 ml-3">
                        <v-icon 
                            class="primary--text d-inline mr-2">
                            mdi-puzzle
                        </v-icon>
                        <div class="d-inline caption">Cross-check attachments and links — make sure all references or embedded files work correctly.</div>
                    </div>

                    <div class="d-flex d-block align-center mt-3">
                        <v-icon  
                            class="error--text text--darken-1 d-inline mr-2">
                            mdi-lightbulb
                        </v-icon>
                        <div class="d-inline">Take a moment to slow down and review — once uploaded, these documents will be visible to the client.</div>
                    </div>
                </div>
            </v-col>
        </v-row>

        <div v-if="changedDeliverableType && email_preview"
            class="mt-6 d-block font-weight-bold d-flex justify-space-between align-center">
            Email Preview
            <v-btn 
                small
                icon
                @click="showEmailPreview = !showEmailPreview">
                <v-icon>{{ showEmailPreview ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </v-btn>
        </div>
        <v-row v-if="showEmailPreview && email_preview" class="mb-1">
            <v-col cols="12" class="px-1">
                <EmailPreviewEditor
                    :key="`deliverable-email-${deliverableType}`"
                    :subject="email_preview.mailSubject || ''"
                    :html="email_preview.mailHtml || ''"
                    :disabled="isUploading"
                    @change="emailEdited" />
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" class="d-flex justify-space-between align-center px-1 mt-2">
                <v-checkbox
                    v-if="(disable_upload && files.length && deliverableType != null) || (!disable_upload && files.length && deliverableType != null)"    
                    class="d-inline justify-start mt-n2"
                    color="primary" 
                    hide-details
                    @click="disable_upload = false"
                    label="I've reviewed the documents and understand it will be visible to the client.">
                </v-checkbox>
                <div v-else class="d-inline flex-grow-1"></div>

                <v-btn
                    class="d-inline justify-end"
                    color="primary lighten-2 info--text"
                    :disabled="disable_upload || isUploading"
                    @click="upload">
                    {{ files.length > 1 ? 'Upload Files' : 'Upload File' }}
                </v-btn>             
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment';
    import { safeParseJson } from '../helpers/safe-json';
    import EmailPreviewEditor from './shared/EmailPreviewEditor.vue';

    export default {
        name: 'UploadDeliverables',

        components: {
            EmailPreviewEditor
        },
        
        props: {
            projectData: Object,
            moduleKey: String,
            projectId: String,
            userId: String,
            isDeliverable: Boolean,
            triggerUserFeedback: Function,
            closeUploadDeliverables: Function,
            refreshDocs: Function,
            extraMsg: String,
            revisionDocCount: Number
        },
        
        data: () => ({
            files: [],
            message: '',
            current_files: [],
            isUploading: false,
            disable_upload: true,
            warning_fileType: false,
            warning_fileSize: false,
            warning_fileDuplicate: false,
            invalid_filetype_details: [],
            file_match: [],
            warning_message: 'There seems to be an issue with the file being uploaded!',
            allowedFileTypes: [
                "application/octet-stream", // general
                "application/msword", // doc
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
                "application/vnd.ms-word.document.macroenabled.12", // docm on Mac
                "application/vnd.ms-word.document.macroEnabled.12", // docm on Windows
                "application/vnd.ms-excel", // xls
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
                "application/vnd.ms-excel.sheet.macroenabled.12", // xlsm on Mac
                "application/vnd.ms-excel.sheet.macroEnabled.12", // xlsm on Windows
                "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
                "application/pdf", // pdf
                "image/jpeg", // jpg
                "image/png", // png
            ],
            deliverableType: null,
            changedDeliverableType: false,
            fileTypeHint: '',
            standard_deliverable_types: [],
            emp_deliverable_types: [],
            rules: {
                required: value => !!value || 'required'
            },
            generateEmail: true,
            showEmailPreview: false,
            email_preview: null,
            emailTemplateFound: true,
            // Admin edits to the previewed email ({ edited, subject, html }); untouched = send the template
            emailOverride: null,
        }),

        computed: {
            isMultiple () {
                return this.revisionDocCount > 1 ? true : false
            },
            
            hintText () {
                return this.revisionDocCount > 1 ? 'You can upload multiple files at once ... Accepted file types: pdf, doc/docx/docm, xls/xlsx/xlsm' : 'Upload only one file ... Accepted file types: pdf, doc/docx/docm, xls/xlsx/xlsm'
            },
            
            labelText () {
                return this.revisionDocCount > 1 ? 'Click to Select Files' : 'Click to Select a File'
            },

            deliverable_types () {
                if ( this.moduleKey === 'entity-maintenance-program'){
                    return this.emp_deliverable_types;
                }
                return this.standard_deliverable_types;
            }

        },

        mounted: function () {
            this.getDeliverableTypeLists();
            // this.fileCheck( 'all_files', this.projectId );
        },

        methods: {
            getDeliverableTypeLists: async function () {
                try {
                    let optionsLists = [ 
                        {'name': 'standard_deliverable_types', 'type': 'txt'},
                        {'name': 'emp_deliverable_types', 'type': 'txt'}
                    ];

                    const get_lists = await axios.post(`${appUrls.api}/api/lists`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        list_options: optionsLists
                    })

                    if ( get_lists.data.outcome ) {
                        this.arrLists = get_lists.data.lists;                        
                        // SET PROPERTIES WITH THIER APPROPRIATE LIST
                        for (let l in this.arrLists) {
                            if (this.arrLists[l].name == 'standard_deliverable_types') this.standard_deliverable_types = this.arrLists[l].list
                            if (this.arrLists[l].name == 'emp_deliverable_types') this.emp_deliverable_types = this.arrLists[l].list
                        }
                    }
                } catch ( error ) {
                    console.log(error);
                };
            },
            
            fileCheck: async function (fieldData, projectId) {
                try {                    
                    const file_check = await axios.post(`${appUrls.api}/api/file_check`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        fieldData, 
                        projectId
                    })
                    
                    this.current_files = file_check.data.outcome ? safeParseJson(file_check.data.file_obj, []) : []                    
                } catch (err) {
                    console.log(err)
                    return err
                }
            },

            handleDeliverableTypeChange: async function () {
                this.changedDeliverableType = true
                this.fileTypeHint = ''
                this.emailOverride = null
                // Nothing can be emailed until a template has been found and previewed
                this.setEmailPreview( null )

                try {
                    const get_email_preview = await axios.post(`${appUrls.api}/api/admin/email_preview`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        params: { 
                            moduleKey: this.moduleKey,
                            projectId: this.projectId,
                            userId: this.userId,
                            emailContentKey: this.deliverableType,
                        }
                    })

                    const email_preview = get_email_preview.data.outcome ? get_email_preview.data.email_preview : null
                    const templateFound = !!(email_preview && email_preview.displayDefinitions && email_preview.displayDefinitions.statusFound)
                    this.setEmailPreview( templateFound ? email_preview : null )
                } catch ( err ) {
                    console.log(err)
                    this.setEmailPreview( null )
                }
            },

            // No template in temailcontent = no preview and no email
            setEmailPreview: function ( email_preview ) {
                this.email_preview = email_preview
                this.emailTemplateFound = !!email_preview
                this.generateEmail = !!email_preview
                this.showEmailPreview = !!email_preview
            },

            emailEdited: function ( change ) {
                this.emailOverride = change.edited ? change : null
            },

            handleChange: function (files) {
                if ( this.deliverableType == null && this.changedDeliverableType == false ) return this.fileTypeHint = 'Please select the type of deliverable being uploaded!'

                if ( files ) {
                    this.warning_message = 'There seems to be an issue with the file being uploaded!'
                    this.invalid_filetype_details = []
                    this.file_match = []

                    const invalid_file = files.some( file => {
                        return !this.allowedFileTypes.includes(file.type) || file.size == 0 || this.current_files.some( current => current.filename == file.name )
                    })

                    if ( !invalid_file ) {
                        // Save a reference to the file blob.
                        // We will use this later for uploading.
                        this.disable_upload = true
                        this.warning_fileType = false
                        this.warning_fileDuplicate = false
                        this.files = files
                    } else {
                        this.disable_upload = true

                        files.forEach( file => {
                            if ( !this.allowedFileTypes.includes(file.type) ) {
                                this.warning_fileType = true
                                this.invalid_filetype_details.push(file)
                            }
                            
                            if ( file.size == 0 ) {
                                this.warning_fileSize = true
                            }

                            if ( this.current_files.some( current => current.filename == file.name ) ) {
                                this.current_files.forEach( current => {
                                    if ( current.filename == file.name ) {
                                        this.file_match.push(current.filename)
                                    }
                                })
                                this.warning_fileDuplicate = true
                            }
                        })
                        
                        if ( this.invalid_filetype_details.length ) { 
                            this.warning_message = `The file type <strong>${this.invalid_filetype_details[0].type}</strong> is not permitted for uploading! Allowed file types include: pdf, doc/docx/docm, xls/xlsx/xlsm`
                        }
                            
                        if ( this.warning_fileSize ) {
                            this.warning_message = `The file size is 0mb, please check the file and upload a file with a larger size.`
                        }

                        if ( this.warning_fileDuplicate ) {
                            if ( this.file_match.length == 1 ) {
                                this.warning_message = `This file: <strong>${this.file_match}</strong> seems to be a duplicate, please delete the file before uploading the new one.`
                            } else {
                                this.warning_message = `These files: <strong>${this.file_match.join(', ')}</strong> seem to be a duplicates, please delete these files before uploading the new ones.`
                            }
                        }
                        
                        this.files = []
                    }
                }
            },

            upload: async function () {
                // ONE REQUEST AT A TIME - A DOUBLE-CLICK USED TO UPLOAD (AND EMAIL) THE SAME FILE TWICE
                if ( this.isUploading ) return

                if ( !this.deliverableType ) return this.fileTypeHint = 'Please select the type of deliverable being uploaded!'

                this.isUploading = true;
                
                const formData = new FormData(); // document.getElementById('form')
                this.files.forEach( file => {
                    // console.log(file)
                    return formData.append('files', file);
                })
                formData.append('authentication', Cookie.get('token'));
                formData.append('moduleKey', this.moduleKey);
                formData.append('userId', this.userId);
                formData.append('projectId', this.projectId);
                formData.append('isDeliverable', this.isDeliverable);
                formData.append('deliverableType', this.deliverableType);
                formData.append('generateEmail', this.generateEmail);
                if ( this.generateEmail && this.emailOverride ) {
                    if ( this.emailOverride.subject ) formData.append('emailSubjectOverride', this.emailOverride.subject);
                    if ( this.emailOverride.html ) formData.append('emailHtmlOverride', this.emailOverride.html);
                }
                formData.append('source', 'admin');
                // console.log(...formData)

                try {
                    const res = await axios.post(`${appUrls.api}/api/upload_file`, formData)

                    if ( res.data.outcome === false ) {
                        throw new Error(res.data.message);
                    }

                    this.$emit('closeUploadDeliverables');
                    this.isUploading = false;
                    this.file = null;
                    this.$emit('triggerUserFeedback', true, 'File was successfully uploaded!' );

                    setTimeout(() => { 
                        this.$emit('refreshDocs', this.projectData);
                        this.deliverableType=''
                        this.changedDeliverableType = false
                        this.emailOverride = null
                        this.disable_upload = true;
                        this.files=[]
                    }, 1500)

                } catch (err) {
                    setTimeout(() => {
                        this.$emit('closeUploadDeliverables');
                        this.deliverableType = ''
                        this.changedDeliverableType = false
                        this.emailOverride = null
                        this.isUploading = false;
                        this.disable_upload = true;
                        this.file = null;
                        this.$emit('triggerUserFeedback', true, err.message, 'accent' );
                    }, 2000)
                }
            },

            cancel: function () {
                // Reset the control on cancel so it loads fresh next time
                this.deliverableType = ''
                this.changedDeliverableType = false
                this.emailOverride = null
                this.isUploading = false;
                this.disable_upload = true;
                this.files = null;

                this.$emit('closeUploadDeliverables');
            }
        }
    }
</script>

<style scoped>
    .required_rule >>> .v-messages__message {
        color:red;
    }
</style>


// WEBPACK FOOTER //
// src/components/UploadDeliverables.vue